import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { GmailAuth } from './GmailAuth';
import GmailService from '../services/GmailService';

interface EmailManagementProps {
  selectedSemesterId: number | null;
  selectedSeason: string;
  selectedYear: number;
}

interface Assignment {
  id: number;
  family_id: number;
  student_id: number;
  semester_id: number;
  family_email_sent_at: string | null;
  student_email_sent_at: string | null;
}

interface Student {
  id: number;
  first_name: string;
  family_name: string;
  email: string;
  home_country: string;
  address: string;
  phone_number: string;
  major: string;
  program_level: string;
  special_interests: string;
  has_dietary_restrictions: boolean;
  dietary_restrictions_details: string;
  vaccination_status: string;
  gender: string;
  marital_status: string;
}

interface Family {
  id: number;
  first_name: string;
  last_name: string;
  combined_name: string;
  email: string;
  address: string;
  phone_number: string;
}

export const EmailManagement: React.FC<EmailManagementProps> = ({
  selectedSemesterId,
  selectedSeason,
  selectedYear
}) => {
  const [isGmailAuthenticated, setIsGmailAuthenticated] = useState(false);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [families, setFamilies] = useState<Family[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [previewType, setPreviewType] = useState('family'); // 'family' or 'student'
  const [previewAssignmentId, setPreviewAssignmentId] = useState<number | null>(null);
  
  // Email template state
  const [familyEmailTemplate, setFamilyEmailTemplate] = useState('');
  const [studentEmailTemplate, setStudentEmailTemplate] = useState('');
  const [familyEmailSubject, setFamilyEmailSubject] = useState('');
  const [studentEmailSubject, setStudentEmailSubject] = useState('');

  const gmailService = GmailService.getInstance();
  const currentSemester = `${selectedSeason} ${selectedYear}`;

  // Default email templates
  const defaultFamilyTemplate = `{family_name},

<p>Thank you for your family's involvement with SIUE's International Hospitality Program. Below are your family's {student_count} student assignments for the {semester} semester. If there are any issues with the assignments, reach out and let us know. Please make sure to contact your student(s) within a week to introduce yourself.</p>

<p>We are gathering important information and resources into one Google Doc. We will continue to add resources as they are made available to us, so make sure to check the document periodically. You can find it here: <a href="https://docs.google.com/document/d/1CKZBVGuREXohpbLjF_OwMkMU0D3nQngHbtmGFP4jFWE/edit?usp=sharing">https://docs.google.com/document/d/1CKZBVGuREXohpbLjF_OwMkMU0D3nQngHbtmGFP4jFWE/edit?usp=sharing</a></p>

<p>IHP Student Assignment Committee</p>`;

  const defaultStudentTemplate = `{student_name},

<p>Thank you for signing up for SIUE's International Hospitality Program. Below is the information for your host family, {family_name}. If there are any issues with the assignments, reach out and let us know.</p>

<p>Your host family also received a similar email with the information you filled out on the "Request a host family" form (your name, address, phone number, major, etc). They should be reaching out to you soon, but you are welcome to message them first and introduce yourself.</p>

<p>IHP Student Assignment Committee</p>`;

  const loadData = useCallback(async () => {
    if (!selectedSemesterId) return;
    
    setLoading(true);
    try {
      const [assignmentsResult, studentsResult, familiesResult] = await Promise.all([
        supabase
          .from('assignments')
          .select('*')
          .eq('semester_id', selectedSemesterId)
          .order('id'),
        supabase
          .from('students')
          .select('*'),
        supabase
          .from('families')
          .select('*')
          .eq('status', 'Active')
      ]);

      if (assignmentsResult.data) setAssignments(assignmentsResult.data);
      if (studentsResult.data) setStudents(studentsResult.data);
      if (familiesResult.data) setFamilies(familiesResult.data);
    } catch (error) {
      console.error('Error loading email data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedSemesterId]);

  // Template management functions
  const loadEmailTemplates = useCallback(async () => {
    if (!selectedSemesterId) return;

    try {
      // Load templates from database for current semester
      const { data: templates, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('semester_id', selectedSemesterId);

      if (error) {
        console.error('Error loading email templates:', error);
        // Fallback to default templates
        setFamilyEmailTemplate(defaultFamilyTemplate);
        setStudentEmailTemplate(defaultStudentTemplate);
        setFamilyEmailSubject(`IHP Assignments ${currentSemester}`);
        setStudentEmailSubject(`IHP Host Family Assignment ${currentSemester}`);
        return;
      }

      // Find family and student templates
      const familyTemplate = templates?.find(t => t.template_type === 'family');
      const studentTemplate = templates?.find(t => t.template_type === 'student');

      console.log('loadEmailTemplates result:', {
        templatesCount: templates?.length || 0,
        familyTemplate: familyTemplate ? 'FOUND' : 'NOT_FOUND',
        studentTemplate: studentTemplate ? 'FOUND' : 'NOT_FOUND',
        familyBody: familyTemplate?.body ? familyTemplate.body.substring(0, 100) + '...' : 'NO_BODY',
        studentBody: studentTemplate?.body ? studentTemplate.body.substring(0, 100) + '...' : 'NO_BODY',
        defaultFamilyTemplate: defaultFamilyTemplate ? defaultFamilyTemplate.substring(0, 100) + '...' : 'NO_DEFAULT'
      });

      // Set templates or fallback to defaults
      setFamilyEmailTemplate(familyTemplate?.body || defaultFamilyTemplate);
      setStudentEmailTemplate(studentTemplate?.body || defaultStudentTemplate);
      setFamilyEmailSubject(familyTemplate?.subject || `IHP Assignments ${currentSemester}`);
      setStudentEmailSubject(studentTemplate?.subject || `IHP Host Family Assignment ${currentSemester}`);

    } catch (error) {
      console.error('Error loading email templates:', error);
      // Fallback to default templates
      setFamilyEmailTemplate(defaultFamilyTemplate);
      setStudentEmailTemplate(defaultStudentTemplate);
      setFamilyEmailSubject(`IHP Assignments ${currentSemester}`);
      setStudentEmailSubject(`IHP Host Family Assignment ${currentSemester}`);
    }
  }, [defaultFamilyTemplate, defaultStudentTemplate, currentSemester, selectedSemesterId]);

  const saveEmailTemplates = async () => {
    if (!selectedSemesterId) {
      alert('No semester selected');
      return;
    }

    try {
      // Save family template
      const { error: familyError } = await supabase
        .from('email_templates')
        .upsert({
          semester_id: selectedSemesterId,
          template_type: 'family' as const,
          subject: familyEmailSubject,
          body: familyEmailTemplate,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'semester_id, template_type'
        });

      if (familyError) {
        console.error('Error saving family template:', familyError);
        alert('❌ Error saving family template: ' + familyError.message);
        return;
      }

      // Save student template
      const { error: studentError } = await supabase
        .from('email_templates')
        .upsert({
          semester_id: selectedSemesterId,
          template_type: 'student' as const,
          subject: studentEmailSubject,
          body: studentEmailTemplate,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'semester_id, template_type'
        });

      if (studentError) {
        console.error('Error saving student template:', studentError);
        alert('❌ Error saving student template: ' + studentError.message);
        return;
      }

      alert('✅ Email templates saved to database successfully!');
    } catch (error: any) {
      console.error('Error saving email templates:', error);
      alert('❌ Error saving templates: ' + error.message);
    }
  };

  const resetTemplates = () => {
    if (window.confirm('Are you sure you want to reset templates to default? This will lose any custom changes.')) {
      setFamilyEmailTemplate(defaultFamilyTemplate);
      setStudentEmailTemplate(defaultStudentTemplate);
      setFamilyEmailSubject(`IHP Assignments ${currentSemester}`);
      setStudentEmailSubject(`IHP Host Family Assignment ${currentSemester}`);
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      await loadData();
      await loadEmailTemplates();
    };
    loadAll();
  }, [loadData, loadEmailTemplates]);

  const handleGmailAuthChange = (isAuthenticated: boolean, email: string) => {
    setIsGmailAuthenticated(isAuthenticated);
  };

  const getStudentById = (studentId: number) => {
    return students.find(s => s.id === studentId);
  };

  const getFamilyById = (familyId: number) => {
    return families.find(f => f.id === familyId);
  };

  // Email template functions (based on your existing Google Apps Script)
  const formatStudentInfo = (student: Student) => {
    return `
      <p><strong>Name:</strong> ${student.first_name} ${student.family_name}</p>
      <p><strong>Phone Number:</strong> ${student.phone_number || 'Not provided'}</p>
      <p><strong>Email:</strong> ${student.email}</p>
      <p><strong>Home Country:</strong> ${student.home_country || 'Not provided'}</p>
      <p><strong>Address:</strong> ${student.address || 'Not provided'}</p>
      <p><strong>Gender:</strong> ${student.gender || 'Not provided'}</p>
      <p><strong>Marital Status:</strong> ${student.marital_status || 'Not provided'}</p>
      <p><strong>Major:</strong> ${student.major || 'Not provided'}</p>
      <p><strong>Program Level:</strong> ${student.program_level || 'Not provided'}</p>
      <p><strong>Special Interests:</strong> ${student.special_interests || 'Not provided'}</p>
      <p><strong>Has Dietary Restrictions:</strong> ${student.has_dietary_restrictions ? 'Yes' : 'No'}</p>
      <p><strong>Dietary Restrictions (if applicable):</strong> ${student.dietary_restrictions_details || 'None'}</p>
      <p><strong>Vaccination Status:</strong> ${student.vaccination_status || 'Not provided'}</p>
    `;
  };

  const createFamilyEmailBody = (assignedStudents: Student[], family?: Family) => {
    // Use the editable template and substitute variables (fallback to default if empty)
    const templateToUse = familyEmailTemplate || defaultFamilyTemplate;
    let processedTemplate = templateToUse
      .replace(/\{family_name\}/g, family ? `${family.first_name} ${family.last_name}` : 'Family')
      .replace(/\{semester\}/g, currentSemester)
      .replace(/\{student_count\}/g, assignedStudents.length.toString());

    let studentsInfo = '';
    assignedStudents.forEach((student, index) => {
      if (index > 0) studentsInfo += '<br><hr><br>';
      studentsInfo += formatStudentInfo(student);
    });

    return processedTemplate + '<br><hr><br>' + studentsInfo;
  };

  const createStudentEmailBody = (student: Student, family: Family) => {
    // Use the editable template and substitute variables (fallback to default if empty)
    const templateToUse = studentEmailTemplate || defaultStudentTemplate;
    let processedTemplate = templateToUse
      .replace(/\{student_name\}/g, `${student.first_name} ${student.family_name}`)
      .replace(/\{family_name\}/g, `${family.first_name} ${family.last_name}`)
      .replace(/\{family_email\}/g, family.email)
      .replace(/\{semester\}/g, currentSemester);

    // Add family information section (only name and email for privacy)
    const familyInfo = `
      <br><hr>
      <h3>Your Host Family Information:</h3>
      <p><strong>Family Name:</strong> ${family.first_name} ${family.last_name}</p>
      <p><strong>Email:</strong> ${family.email}</p>
    `;

    return processedTemplate + familyInfo;
  };

  // Preview generation functions
  const generateEmailPreview = () => {
    if (!previewAssignmentId) {
      return {
        subject: 'No assignment selected',
        body: '<p>Please select an assignment to preview the email content.</p>'
      };
    }

    const assignment = assignments.find(a => a.id === previewAssignmentId);
    if (!assignment) {
      return {
        subject: 'Assignment not found',
        body: '<p>Selected assignment not found.</p>'
      };
    }

    const student = getStudentById(assignment.student_id);
    const family = getFamilyById(assignment.family_id);

    if (!student || !family) {
      return {
        subject: 'Student or family not found',
        body: '<p>Could not find student or family data for this assignment.</p>'
      };
    }

    if (previewType === 'family') {
      // Preview family email (shows student info)
      const studentsForFamily = assignments
        .filter(a => a.family_id === assignment.family_id)
        .map(a => getStudentById(a.student_id))
        .filter(s => s !== undefined) as Student[];
      
      return {
        subject: familyEmailSubject || `IHP Assignments ${currentSemester}`,
        body: createFamilyEmailBody(studentsForFamily, family)
      };
    } else {
      // Preview student email (shows family info)
      return {
        subject: studentEmailSubject || `IHP Host Family Assignment ${currentSemester}`,
        body: createStudentEmailBody(student, family)
      };
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      alert('Please enter a test email address');
      return;
    }

    if (!isGmailAuthenticated) {
      alert('Please connect to Gmail first');
      return;
    }

    if (!previewAssignmentId) {
      alert('Please select an assignment to preview first');
      return;
    }

    setSendingEmails(true);
    try {
      // Use the currently previewed email content
      const previewContent = generateEmailPreview();
      
      // Get the assignment details to show who the original recipient would be
      const assignment = assignments.find(a => a.id === previewAssignmentId);
      const student = assignment ? getStudentById(assignment.student_id) : null;
      const family = assignment ? getFamilyById(assignment.family_id) : null;
      
      let originalRecipient = 'Unknown';
      if (previewType === 'family' && family) {
        originalRecipient = family.email;
      } else if (previewType === 'student' && student) {
        originalRecipient = student.email;
      }
      
      console.log('Sending test email to:', testEmail);
      console.log('Using previewed email content');
      
      console.log('About to send test email with content:', {
        to: originalRecipient,
        subject: previewContent.subject,
        htmlBodyLength: previewContent.body.length,
        htmlBodyPreview: previewContent.body.substring(0, 200) + '...'
      });

      const result = await gmailService.sendTestEmail(testEmail, {
        to: originalRecipient,
        subject: previewContent.subject,
        htmlBody: previewContent.body
      });

      if (result.success) {
        alert(`✅ Test email sent successfully to ${testEmail}!\n\nEmail ID: ${result.id}\n\nThis was the ${previewType} email you were previewing.`);
        console.log('Test email sent successfully:', result);
      } else {
        alert(`❌ Failed to send test email: ${result.error}`);
        console.error('Test email failed:', result);
      }
    } catch (error: any) {
      alert(`❌ Error sending test email: ${error.message}`);
      console.error('Test email error:', error);
    } finally {
      setSendingEmails(false);
    }
  };

  // Bulk email sending functions
  const sendBulkFamilyEmails = async () => {
    if (!isGmailAuthenticated) {
      alert('Please connect to Gmail first');
      return;
    }

    // Get families that haven't been sent emails yet
    const unsentFamilyAssignments = assignments.filter(a => a.family_email_sent_at === null);
    const uniqueFamilies = unsentFamilyAssignments
      .filter((assignment, index, arr) => 
        arr.findIndex(a => a.family_id === assignment.family_id) === index
      );

    if (uniqueFamilies.length === 0) {
      alert('All family emails have already been sent for this semester.');
      return;
    }

    const confirmMessage = `Send emails to ${uniqueFamilies.length} families?\n\nThis will send family assignment emails to families who haven't received them yet.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setSendingEmails(true);
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    try {
      for (const assignment of uniqueFamilies) {
        try {
          const family = getFamilyById(assignment.family_id);
          if (!family) {
            errors.push(`Family not found for assignment ${assignment.id}`);
            errorCount++;
            continue;
          }

          // Get all students assigned to this family
          const familyStudents = assignments
            .filter(a => a.family_id === assignment.family_id)
            .map(a => getStudentById(a.student_id))
            .filter(s => s !== undefined) as Student[];

          const emailBody = createFamilyEmailBody(familyStudents, family);
          
          const result = await gmailService.sendEmail({
            to: family.email,
            subject: familyEmailSubject || `IHP Assignments ${currentSemester}`,
            htmlBody: emailBody
          });

          if (result.success) {
            // Update all assignments for this family to mark emails as sent
            const familyAssignmentIds = assignments
              .filter(a => a.family_id === assignment.family_id)
              .map(a => a.id);

            for (const assignmentId of familyAssignmentIds) {
              await supabase
                .from('assignments')
                .update({ family_email_sent_at: new Date().toISOString() })
                .eq('id', assignmentId);
            }

            successCount++;
            console.log(`Family email sent successfully to ${family.email}`);
          } else {
            errors.push(`Failed to send to ${family.email}: ${result.error}`);
            errorCount++;
          }

          // Small delay between emails to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (error: any) {
          errors.push(`Error sending to family: ${error.message}`);
          errorCount++;
        }
      }

      // Reload data to reflect changes
      await loadData();

      // Show results
      let message = `✅ Bulk family email sending complete!\n\n`;
      message += `Successfully sent: ${successCount}\n`;
      message += `Errors: ${errorCount}\n`;
      
      if (errors.length > 0) {
        message += `\nErrors:\n${errors.slice(0, 5).join('\n')}`;
        if (errors.length > 5) {
          message += `\n... and ${errors.length - 5} more errors`;
        }
      }

      alert(message);
    } catch (error: any) {
      alert(`Error during bulk sending: ${error.message}`);
    } finally {
      setSendingEmails(false);
    }
  };

  const sendBulkStudentEmails = async () => {
    if (!isGmailAuthenticated) {
      alert('Please connect to Gmail first');
      return;
    }

    // Get assignments where students haven't been sent emails yet
    const unsentStudentAssignments = assignments.filter(a => a.student_email_sent_at === null);

    if (unsentStudentAssignments.length === 0) {
      alert('All student emails have already been sent for this semester.');
      return;
    }

    const confirmMessage = `Send emails to ${unsentStudentAssignments.length} students?\n\nThis will send host family information emails to students who haven't received them yet.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setSendingEmails(true);
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    try {
      for (const assignment of unsentStudentAssignments) {
        try {
          const student = getStudentById(assignment.student_id);
          const family = getFamilyById(assignment.family_id);

          if (!student || !family) {
            errors.push(`Student or family not found for assignment ${assignment.id}`);
            errorCount++;
            continue;
          }

          const emailBody = createStudentEmailBody(student, family);
          
          const result = await gmailService.sendEmail({
            to: student.email,
            subject: studentEmailSubject || `IHP Host Family Assignment ${currentSemester}`,
            htmlBody: emailBody
          });

          if (result.success) {
            // Update assignment to mark student email as sent
            await supabase
              .from('assignments')
              .update({ student_email_sent_at: new Date().toISOString() })
              .eq('id', assignment.id);

            successCount++;
            console.log(`Student email sent successfully to ${student.email}`);
          } else {
            errors.push(`Failed to send to ${student.email}: ${result.error}`);
            errorCount++;
          }

          // Small delay between emails to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (error: any) {
          errors.push(`Error sending to student: ${error.message}`);
          errorCount++;
        }
      }

      // Reload data to reflect changes
      await loadData();

      // Show results
      let message = `✅ Bulk student email sending complete!\n\n`;
      message += `Successfully sent: ${successCount}\n`;
      message += `Errors: ${errorCount}\n`;
      
      if (errors.length > 0) {
        message += `\nErrors:\n${errors.slice(0, 5).join('\n')}`;
        if (errors.length > 5) {
          message += `\n... and ${errors.length - 5} more errors`;
        }
      }

      alert(message);
    } catch (error: any) {
      alert(`Error during bulk sending: ${error.message}`);
    } finally {
      setSendingEmails(false);
    }
  };

  const getEmailStats = () => {
    const totalAssignments = assignments.length;
    const familyEmailsSent = assignments.filter(a => a.family_email_sent_at !== null).length;
    const studentEmailsSent = assignments.filter(a => a.student_email_sent_at !== null).length;
    const pendingFamilyEmails = assignments.filter(a => a.family_email_sent_at === null).length;
    const pendingStudentEmails = assignments.filter(a => a.student_email_sent_at === null).length;

    return {
      totalAssignments,
      familyEmailsSent,
      studentEmailsSent,
      pendingFamilyEmails,
      pendingStudentEmails
    };
  };

  if (loading) return <div style={{ padding: '20px' }}>Loading email management...</div>;

  const emailStats = getEmailStats();

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2>Email Management - {currentSemester}</h2>
      </div>

      {/* Gmail Authentication */}
      <div style={{ marginBottom: '20px' }}>
        <GmailAuth onAuthChange={handleGmailAuthChange} />
      </div>

      {/* Email Statistics */}
      <div style={{ 
        marginBottom: '20px', 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '15px' 
      }}>
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#e7f3ff', 
          borderRadius: '8px', 
          textAlign: 'center' 
        }}>
          <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#0066cc' }}>
            {emailStats.totalAssignments}
          </div>
          <div style={{ color: '#0066cc' }}>Total Assignments</div>
        </div>
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#d4edda', 
          borderRadius: '8px', 
          textAlign: 'center' 
        }}>
          <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#155724' }}>
            {emailStats.familyEmailsSent}
          </div>
          <div style={{ color: '#155724' }}>Family Emails Sent</div>
        </div>
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#d4edda', 
          borderRadius: '8px', 
          textAlign: 'center' 
        }}>
          <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#155724' }}>
            {emailStats.studentEmailsSent}
          </div>
          <div style={{ color: '#155724' }}>Student Emails Sent</div>
        </div>
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#fff3cd', 
          borderRadius: '8px', 
          textAlign: 'center' 
        }}>
          <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#856404' }}>
            {emailStats.pendingFamilyEmails}
          </div>
          <div style={{ color: '#856404' }}>Pending Family Emails</div>
        </div>
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#fff3cd', 
          borderRadius: '8px', 
          textAlign: 'center' 
        }}>
          <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#856404' }}>
            {emailStats.pendingStudentEmails}
          </div>
          <div style={{ color: '#856404' }}>Pending Student Emails</div>
        </div>
      </div>

      {/* Bulk Email Actions */}
      {isGmailAuthenticated && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ marginBottom: '15px' }}>📬 Send Emails</h4>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '15px' 
          }}>
            {/* Send Family Emails */}
            <div style={{
              padding: '20px',
              backgroundColor: '#e7f3ff',
              borderRadius: '8px',
              border: '1px solid #b3d9ff'
            }}>
              <div style={{ marginBottom: '15px' }}>
                <h5 style={{ margin: '0 0 5px 0', color: '#0066cc' }}>👥 Send Family Emails</h5>
                <div style={{ fontSize: '0.9em', color: '#0066cc' }}>
                  Send assignment emails to host families
                </div>
                <div style={{ fontSize: '0.8em', color: '#666', marginTop: '5px' }}>
                  Pending: {emailStats.pendingFamilyEmails > 0 
                    ? `${assignments.filter(a => a.family_email_sent_at === null).filter((assignment, index, arr) => arr.findIndex(a => a.family_id === assignment.family_id) === index).length} families`
                    : 'None'}
                </div>
              </div>
              <button
                onClick={sendBulkFamilyEmails}
                disabled={sendingEmails || emailStats.pendingFamilyEmails === 0}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: (emailStats.pendingFamilyEmails > 0 && !sendingEmails) ? '#007bff' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (emailStats.pendingFamilyEmails > 0 && !sendingEmails) ? 'pointer' : 'not-allowed',
                  fontSize: '1em',
                  fontWeight: 'bold'
                }}
              >
                {sendingEmails ? '📧 Sending...' : 
                 emailStats.pendingFamilyEmails > 0 ? '📤 Send Family Emails' : '✅ All Family Emails Sent'}
              </button>
            </div>

            {/* Send Student Emails */}
            <div style={{
              padding: '20px',
              backgroundColor: '#e7f3ff',
              borderRadius: '8px',
              border: '1px solid #b3d9ff'
            }}>
              <div style={{ marginBottom: '15px' }}>
                <h5 style={{ margin: '0 0 5px 0', color: '#0066cc' }}>🎓 Send Student Emails</h5>
                <div style={{ fontSize: '0.9em', color: '#0066cc' }}>
                  Send host family info to students
                </div>
                <div style={{ fontSize: '0.8em', color: '#666', marginTop: '5px' }}>
                  Pending: {emailStats.pendingStudentEmails > 0 
                    ? `${emailStats.pendingStudentEmails} students`
                    : 'None'}
                </div>
              </div>
              <button
                onClick={sendBulkStudentEmails}
                disabled={sendingEmails || emailStats.pendingStudentEmails === 0}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: (emailStats.pendingStudentEmails > 0 && !sendingEmails) ? '#007bff' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (emailStats.pendingStudentEmails > 0 && !sendingEmails) ? 'pointer' : 'not-allowed',
                  fontSize: '1em',
                  fontWeight: 'bold'
                }}
              >
                {sendingEmails ? '📧 Sending...' : 
                 emailStats.pendingStudentEmails > 0 ? '📤 Send Student Emails' : '✅ All Student Emails Sent'}
              </button>
            </div>
          </div>

          {/* Warning Message */}
          {(emailStats.pendingFamilyEmails > 0 || emailStats.pendingStudentEmails > 0) && (
            <div style={{
              marginTop: '15px',
              padding: '10px',
              backgroundColor: '#fff3cd',
              borderRadius: '6px',
              border: '1px solid #ffeaa7',
              fontSize: '0.9em',
              color: '#856404'
            }}>
              <strong>⚠️ Important:</strong> Make sure your email templates are configured correctly and you've tested them using the preview section before sending bulk emails. These actions cannot be undone.
            </div>
          )}
        </div>
      )}

      {/* Email Template Editor */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h4>📝 Email Templates</h4>
          <button
            onClick={() => setShowTemplateEditor(!showTemplateEditor)}
            style={{
              padding: '8px 16px',
              backgroundColor: showTemplateEditor ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9em'
            }}
          >
            {showTemplateEditor ? 'Hide Editor' : 'Edit Templates'}
          </button>
        </div>

        {showTemplateEditor && (
          <div style={{
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Family Email Template */}
              <div>
                <h5 style={{ marginBottom: '10px', color: '#495057' }}>Family Email Template</h5>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '0.9em' }}>
                    Subject Line:
                  </label>
                  <input
                    type="text"
                    value={familyEmailSubject}
                    onChange={(e) => setFamilyEmailSubject(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      fontSize: '0.9em'
                    }}
                    placeholder="Email subject line"
                  />
                </div>
                <textarea
                  value={familyEmailTemplate}
                  onChange={(e) => setFamilyEmailTemplate(e.target.value)}
                  rows={12}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '0.9em',
                    fontFamily: 'monospace',
                    resize: 'vertical'
                  }}
                  placeholder="Enter family email template..."
                />
                <div style={{ fontSize: '0.8em', color: '#6c757d', marginTop: '5px' }}>
                  Available variables: {'{family_name}'}, {'{semester}'}, {'{student_count}'}<br/>
                  <em>Student details (name, email, phone, etc.) are automatically added below the template</em>
                </div>
              </div>

              {/* Student Email Template */}
              <div>
                <h5 style={{ marginBottom: '10px', color: '#495057' }}>Student Email Template</h5>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '0.9em' }}>
                    Subject Line:
                  </label>
                  <input
                    type="text"
                    value={studentEmailSubject}
                    onChange={(e) => setStudentEmailSubject(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      fontSize: '0.9em'
                    }}
                    placeholder="Email subject line"
                  />
                </div>
                <textarea
                  value={studentEmailTemplate}
                  onChange={(e) => setStudentEmailTemplate(e.target.value)}
                  rows={12}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '0.9em',
                    fontFamily: 'monospace',
                    resize: 'vertical'
                  }}
                  placeholder="Enter student email template..."
                />
                <div style={{ fontSize: '0.8em', color: '#6c757d', marginTop: '5px' }}>
                  Available variables: {'{student_name}'}, {'{family_name}'}, {'{family_email}'}, {'{semester}'}<br/>
                  <em>Family contact details (name and email only) are automatically added below the template</em>
                </div>
              </div>
            </div>

            {/* Template Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'center' }}>
              <button
                onClick={saveEmailTemplates}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                  fontWeight: 'bold'
                }}
              >
                💾 Save Templates
              </button>
              <button
                onClick={resetTemplates}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9em'
                }}
              >
                🔄 Reset to Default
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Email Preview Section */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h4>👁️ Email Preview</h4>
          <button
            onClick={() => setShowEmailPreview(!showEmailPreview)}
            style={{
              padding: '8px 16px',
              backgroundColor: showEmailPreview ? '#6c757d' : '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9em'
            }}
          >
            {showEmailPreview ? 'Hide Preview' : 'Preview Emails'}
          </button>
        </div>

        {showEmailPreview && assignments.length > 0 && (
          <div style={{
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6'
          }}>
            {/* Preview Controls */}
            <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '0.9em' }}>
                  Email Type:
                </label>
                <select
                  value={previewType}
                  onChange={(e) => {
                    setPreviewType(e.target.value as 'family' | 'student');
                    setPreviewAssignmentId(null); // Clear selection when switching types
                  }}
                  style={{
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '0.9em'
                  }}
                >
                  <option value="family">Family Email (shows student info)</option>
                  <option value="student">Student Email (shows family info)</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '0.9em' }}>
                  {previewType === 'family' ? 'Family to Preview:' : 'Assignment to Preview:'}
                </label>
                <select
                  value={previewAssignmentId || ''}
                  onChange={(e) => setPreviewAssignmentId(Number(e.target.value) || null)}
                  style={{
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '0.9em',
                    minWidth: '250px'
                  }}
                >
                  <option value="">
                    {previewType === 'family' ? 'Select a family...' : 'Select an assignment...'}
                  </option>
                  {previewType === 'family' ? (
                    // For family emails, group by family and show only family names
                    assignments
                      .filter((assignment, index, arr) => 
                        arr.findIndex(a => a.family_id === assignment.family_id) === index
                      )
                      .map(assignment => {
                        const family = getFamilyById(assignment.family_id);
                        const studentCount = assignments.filter(a => a.family_id === assignment.family_id).length;
                        return (
                          <option key={assignment.family_id} value={assignment.id}>
                            {family?.first_name} {family?.last_name} ({studentCount} student{studentCount !== 1 ? 's' : ''})
                          </option>
                        );
                      })
                  ) : (
                    // For student emails, show individual assignments
                    assignments.map(assignment => {
                      const student = getStudentById(assignment.student_id);
                      const family = getFamilyById(assignment.family_id);
                      return (
                        <option key={assignment.id} value={assignment.id}>
                          {student?.first_name} {student?.family_name} → {family?.first_name} {family?.last_name}
                        </option>
                      );
                    })
                  )}
                </select>
              </div>
            </div>

            {/* Email Preview Display */}
            {previewAssignmentId && (
              <div style={{
                border: '2px solid #007bff',
                borderRadius: '8px',
                backgroundColor: 'white',
                overflow: 'hidden'
              }}>
                {/* Email Header */}
                <div style={{
                  padding: '15px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  borderBottom: '1px solid #0056b3'
                }}>
                  <h5 style={{ margin: 0, marginBottom: '5px' }}>
                    📧 {previewType === 'family' ? 'Family' : 'Student'} Email Preview
                  </h5>
                  <div style={{ fontSize: '0.9em', opacity: '0.9' }}>
                    <strong>Subject:</strong> {generateEmailPreview().subject}
                  </div>
                </div>

                {/* Email Body */}
                <div style={{
                  padding: '20px',
                  maxHeight: '500px',
                  overflowY: 'auto',
                  fontSize: '0.95em',
                  lineHeight: '1.5'
                }}>
                  <div 
                    dangerouslySetInnerHTML={{ __html: generateEmailPreview().body }}
                    style={{
                      fontFamily: 'Arial, sans-serif'
                    }}
                  />
                </div>

                {/* Test Email Section - only show if Gmail is connected */}
                {isGmailAuthenticated && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    borderTop: '1px solid #dee2e6'
                  }}>
                    <h6 style={{ margin: '0 0 10px 0', color: '#495057' }}>🧪 Test Send This Email</h6>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="email"
                        placeholder="Enter test email address"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                          minWidth: '200px',
                          fontSize: '0.9em'
                        }}
                      />
                      <button
                        onClick={handleSendTestEmail}
                        disabled={!testEmail || sendingEmails || !previewAssignmentId}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: (testEmail && !sendingEmails && previewAssignmentId) ? '#17a2b8' : '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: (testEmail && !sendingEmails && previewAssignmentId) ? 'pointer' : 'not-allowed',
                          fontSize: '0.9em'
                        }}
                      >
                        {sendingEmails ? '📧 Sending...' : 'Send Test Email'}
                      </button>
                    </div>
                    <div style={{ fontSize: '0.8em', color: '#6c757d', marginTop: '5px' }}>
                      This will send the exact email content shown above to your test address
                    </div>
                  </div>
                )}
              </div>
            )}

            {!previewAssignmentId && (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#6c757d',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '2px dashed #dee2e6'
              }}>
                <div style={{ fontSize: '2em', marginBottom: '10px' }}>📧</div>
                <div>Select an assignment above to preview the email content</div>
              </div>
            )}
          </div>
        )}

        {showEmailPreview && assignments.length === 0 && (
          <div style={{
            padding: '20px',
            backgroundColor: '#fff3cd',
            borderRadius: '8px',
            border: '1px solid #ffeaa7',
            textAlign: 'center',
            color: '#856404'
          }}>
            <div style={{ fontSize: '1.2em', marginBottom: '5px' }}>⚠️ No Assignments Available</div>
            <div>No assignments found for the current semester. Preview will be available once assignments are created.</div>
          </div>
        )}
      </div>


      {/* Assignment List */}
      <div style={{ marginBottom: '20px' }}>
        <h3>Assignments ({assignments.length})</h3>
        {assignments.length === 0 ? (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            color: '#666',
            fontStyle: 'italic' 
          }}>
            No assignments found for {currentSemester}
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gap: '10px' 
          }}>
            {assignments.map(assignment => {
              const student = getStudentById(assignment.student_id);
              const family = getFamilyById(assignment.family_id);
              
              if (!student || !family) return null;
              
              return (
                <div 
                  key={assignment.id}
                  style={{
                    padding: '15px',
                    border: '1px solid #dee2e6',
                    borderRadius: '8px',
                    backgroundColor: 'white'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ flex: 1, minWidth: '300px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                        {student.first_name} {student.family_name} ({student.home_country}) → {family.combined_name}
                      </div>
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        Student: {student.email} | Family: {family.email}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <span style={{ 
                        padding: '4px 8px', 
                        borderRadius: '12px', 
                        fontSize: '0.8em',
                        backgroundColor: assignment.family_email_sent_at ? '#d4edda' : '#fff3cd',
                        color: assignment.family_email_sent_at ? '#155724' : '#856404'
                      }}>
                        Family: {assignment.family_email_sent_at ? '✅ Sent' : '⏳ Pending'}
                        {assignment.family_email_sent_at && (
                          <div style={{ fontSize: '0.7em', marginTop: '2px' }}>
                            {new Date(assignment.family_email_sent_at).toLocaleDateString()}
                          </div>
                        )}
                      </span>
                      <span style={{ 
                        padding: '4px 8px', 
                        borderRadius: '12px', 
                        fontSize: '0.8em',
                        backgroundColor: assignment.student_email_sent_at ? '#d4edda' : '#fff3cd',
                        color: assignment.student_email_sent_at ? '#155724' : '#856404'
                      }}>
                        Student: {assignment.student_email_sent_at ? '✅ Sent' : '⏳ Pending'}
                        {assignment.student_email_sent_at && (
                          <div style={{ fontSize: '0.7em', marginTop: '2px' }}>
                            {new Date(assignment.student_email_sent_at).toLocaleDateString()}
                          </div>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};